// These libraries are needed only after an explicit export action. Keep both
// imports here so every caller shares webpack's chunks, not the startup bundle.
export const EXPORT_LIBRARY_TIMEOUT_MS = 30_000;

export class ExportLibraryLoadError extends Error {
  constructor() {
    super(
      "Export tools could not be loaded. Check your connection and try again. If this continues, save any edits before reloading the page."
    );
    this.name = "ExportLibraryLoadError";
  }
}

/**
 * Bound the caller's wait; a browser chunk download itself cannot be cancelled.
 * Late module resolution does not resume an expired export. No rejected
 * application promise is cached, so a later explicit action can try again.
 * @template T
 * @param {() => Promise<T>} load
 * @returns {Promise<T>}
 */
export const loadExportLibrary = async (load) => {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let timer;
  /** @type {Promise<never>} */
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new ExportLibraryLoadError()), EXPORT_LIBRARY_TIMEOUT_MS);
  });
  try {
    return await Promise.race([Promise.resolve().then(load), timeout]);
  } catch {
    // Never expose a chunk URL, stack trace, or capability through UI errors.
    throw new ExportLibraryLoadError();
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
};

export const loadZipLibrary = () =>
  loadExportLibrary(async () => {
    const module = await import(/* webpackChunkName: "photo-zip" */ "jszip");
    if (typeof module.default !== "function") throw new Error("Invalid ZIP module");
    return module.default;
  });

export const loadPngExporter = () =>
  loadExportLibrary(async () => {
    const module = await import(/* webpackChunkName: "snapshot-image" */ "html-to-image");
    if (typeof module.toPng !== "function") throw new Error("Invalid image module");
    return module.toPng;
  });
